const STANDARD_BULK_ACTIVITIES = {
  APPROVE: "EDIT",
  MARKAS: "EDIT",
  EDIT: "EDIT",
  BULKEDIT: "EDIT",
  CANCEL: "TXCANCEL",
  DELETE: "DELETE",
  PRINT: "PRINT"
};

export function resolveBulkRequiredActivity(action, bulkConfiguration = {}) {
  const explicitActivity = action?.sRequiredActivity || action?.requiredActivity;
  if (explicitActivity) return String(explicitActivity).trim().toUpperCase();

  const choice = String(action?.sChoice || action?.action || "").trim();
  const configuredActivity = bulkConfiguration?.sPermissionMapping?.[choice];
  if (configuredActivity) return String(configuredActivity).trim().toUpperCase();

  const normalizedChoice = choice.replace(/[^a-z]/gi, "").toUpperCase();
  if (STANDARD_BULK_ACTIVITIES[normalizedChoice]) {
    return STANDARD_BULK_ACTIVITIES[normalizedChoice];
  }

  const url = String(action?.sValue || "").toUpperCase();
  const matchedAction = Object.keys(STANDARD_BULK_ACTIVITIES)
    .find(candidate => url.includes(`/${candidate.toLowerCase()}`.toUpperCase()));
  return matchedAction ? STANDARD_BULK_ACTIVITIES[matchedAction] : null;
}

export function createBulkOperationId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

export function isBulkProgressEnabled(action, bulkConfiguration = {}) {
  const url = String(action?.sValue || "");
  const isTransactionBulkAction = /\/(purchase|sales)\/[^/?]+\/bulk\//i.test(url);
  return isTransactionBulkAction || bulkConfiguration?.sProgressEnabled === true;
}

export function resolveBulkWorkflowVerification(action, bulkConfiguration = {}) {
  const requiredActivity = resolveBulkRequiredActivity(action, bulkConfiguration);
  switch (requiredActivity) {
    case "EDIT": return "edit";
    case "TXCANCEL": return "cancel";
    case "DELETE": return "delete";
    case "PRINT": return "print";
    case "VIEW": return "view";
    default: return null;
  }
}

export function hasBulkSelection(selectedRows, minimumRows = 2) {
  return Array.isArray(selectedRows) && selectedRows.length >= minimumRows;
}

export function isBulkActionAllowedForRows(action, selectedRows, options = {}) {
  if (!Array.isArray(selectedRows) || selectedRows.length === 0) return false;

  const targetStatusMatch = String(action?.sValue || "").match(/[?&]status=([^&]*)/i);
  if (targetStatusMatch) {
    let targetStatus = targetStatusMatch[1];
    try {
      targetStatus = decodeURIComponent(targetStatus.replace(/\+/g, " "));
    } catch (_) {
      // Keep the raw configured value when it is not URI encoded correctly.
    }
    const statusFieldName = options.statusFieldName || "sDocStatus";
    if (selectedRows.some(row =>
      String(row?.[statusFieldName] ?? row?.sDocStatus ?? "").trim().toLowerCase() ===
      String(targetStatus).trim().toLowerCase()
    )) {
      return false;
    }
  }

  if (!options.verifyConfigured) return true;

  const verificationStatuses = Array.isArray(options.verificationStatuses)
    ? options.verificationStatuses
    : [];
  return selectedRows.every(row => {
    const configuredPrimaryValue = options.primaryKey
      ? row?.[options.primaryKey]
      : undefined;
    const recordId = configuredPrimaryValue ?? row?.sDocNumber ?? row?.id;
    const verification = verificationStatuses.find(item =>
      String(item?.id) === String(recordId)
    );
    return verification?.status === false;
  });
}

export function formatBulkOperationMessage(payload) {
  const fallbackMessage = String(payload?.metadata?.msg || "").trim();
  const records = Array.isArray(payload?.data?.records) ? payload.data.records : [];
  const failures = records
    .filter(record => record?.success === false)
    .map(record => {
      const primaryKeyValue = String(record?.primaryKeyValue || "Unknown record").trim();
      const message = String(record?.message || record?.errorCode || "Operation failed").trim();
      return `${primaryKeyValue}: ${message}`;
    });

  if (failures.length === 0) return fallbackMessage;
  return fallbackMessage ? `${fallbackMessage}: ${failures.join(", ")}` : failures.join(", ");
}
